import { useEventsPublisher } from 'event-bus';
import { ValidationSchema } from 'validation-schema';
import { FormFieldsData } from 'ui-builder';
import { ShortProjectRole, TaskType } from './Types';
import { useBackendApi } from './backendApi';
import { TasksGroup } from './tasksGroupsService';
import { useObjectDatesMapper } from './Helper';

export enum DueDateWhen {
  BEFORE_START_DATE = 'before_start',
  AFTER_START_DATE = 'after_start',
  BEFORE_END_DATE = 'before_end',
  AFTER_END_DATE = 'after_end',
}

export type DueDateConfig = {
  days: number,
  when: DueDateWhen
}

export type PlanTaskParentTaskRelation = {
  days: number,
  parentTaskId: number
  parentTaskName: string
  isProceeding?: boolean
}

export type TransactionParty = {
  projectRoleId: number,
  remindDays?: number
  _projectRole: ShortProjectRole
}

export enum RecipientType {
  RECIPIENT = 'RECIPIENT',
  CC = 'CC',
  BCC = 'BCC',
}

export type PlanTaskEmailRecipient = {
  projectRoleId: number,
  type: RecipientType,
  _projectRole: ShortProjectRole
}

export type PlanTaskSmsRecipient = {
  projectRoleId: number
  _projectRole: ShortProjectRole
}

export type PlanTaskInviteRecipient = {
  projectRoleId: number
  _projectRole: ShortProjectRole
}

export type   PlanTask = {
  id: number,
  planId: number,
  name: string,
  type: TaskType,
  dueDateConfig?: DueDateConfig,
  isKeyDate: boolean,
  isPrivate: boolean,
  isAiTask: boolean,
  isDuringBusinessWeek: boolean,
  description: string,
  transactionParties: TransactionParty[],
  order: number,
  sendFromRoleId?: number,
  isAutoSent: boolean,
  emailRecipients: PlanTaskEmailRecipient[],
  smsRecipients: PlanTaskSmsRecipient[],
  inviteRecipients: PlanTaskInviteRecipient[],
  location?: string,
  parentTaskRelation?: PlanTaskParentTaskRelation
  useBusinessDaysCalculation: boolean,
  linkedFieldId?: number
  _linkedField?: {
    id: number
    name: string,
  }
  groupId?: number,
  _group?: TasksGroup
  excluded: boolean,
  addTransactionNameAtEnd: boolean,
  _version?: number,
  _dependencyLevel?: number,
  isIncludedToRoadmap?: boolean,
  roadmapDescription?: string,
  _sourcePlan?: {
    id: number,
    name: string,
  }
}

export interface PlanTasksService {
  getPlanTasks: (planId: number) => Promise<PlanTask[]>,
  createStandardTask: (planId: number, data: PlanTask) => Promise<PlanTask>,
  createEmailTask: (planId: number, data: PlanTask) => Promise<PlanTask>,
  createSmsTask: (planId: number, data: PlanTask) => Promise<PlanTask>,
  createEventTask: (planId: number, data: PlanTask) => Promise<PlanTask>,
  updateTask: (taskId: number, data: PlanTask) => Promise<PlanTask>,
  getStandardTaskValidationSchema: () => ValidationSchema,
  getEmailTaskValidationSchema: () => ValidationSchema,
  getSmsTaskValidationSchema: () => ValidationSchema,
  getEventTaskValidationSchema: () => ValidationSchema,
  deleteTask: (taskId: number) => Promise<void>,
  getTasksCompareFunc: () => (task1: PlanTask, task2: PlanTask) => number,
  setOrder: (ids: number[]) => Promise<void>;
}

export const PLAN_TASKS_UPDATED = 'PLAN_TASKS_UPDATED';
export const PLAN_TASK_CREATED = 'PLAN_TASK_CREATED';
export const PLAN_TASK_UPDATED = 'PLAN_TASK_UPDATED';
export const PLAN_TASK_DELETED = 'PLAN_TASK_DELETED';

export interface PlanTaskEvent {
  id: number;
  name: string;
  planId: number;
}

const NO_DUE_DATE_WEIGHT = 2;

export function usePlanTasksService(): PlanTasksService {
  const api = useBackendApi();

  const eventsPublisher = useEventsPublisher();

  const objectMapper = useObjectDatesMapper<PlanTask>(
    [
      'calculatedDueDate',
    ],
    []
  );

  const onPlanTasksUpdated = (planId: number) => {
    eventsPublisher.publish(PLAN_TASKS_UPDATED, {
      planId,
    });
  };

  const createStandardTask = async (planId: number, data: PlanTask) => {
    const result = await api.post(`/plans/${planId}/tasks/standard`, data);

    onPlanTasksUpdated(planId);
    eventsPublisher.publish<PlanTaskEvent>(PLAN_TASK_CREATED, {
      id: result.id,
      name: result.name,
      planId: result.planId,
    });

    return objectMapper.mapSingle(result);
  };

  const createEmailTask = async (planId: number, data: PlanTask) => {
    const result = await api.post(`/plans/${planId}/tasks/email`, data);

    onPlanTasksUpdated(planId);
    eventsPublisher.publish<PlanTaskEvent>(PLAN_TASK_CREATED, {
      id: result.id,
      name: result.name,
      planId: result.planId,
    });

    return objectMapper.mapSingle(result);
  };

  const createSmsTask = async (planId: number, data: PlanTask) => {
    const result = await api.post(`/plans/${planId}/tasks/sms`, data);

    onPlanTasksUpdated(planId);
    eventsPublisher.publish<PlanTaskEvent>(PLAN_TASK_CREATED, {
      id: result.id,
      name: result.name,
      planId: result.planId,
    });

    return objectMapper.mapSingle(result);
  };

  const createEventTask = async (planId: number, data: PlanTask) => {
    const result = await api.post(`/plans/${planId}/tasks/event`, data);

    onPlanTasksUpdated(planId);
    eventsPublisher.publish<PlanTaskEvent>(PLAN_TASK_CREATED, {
      id: result.id,
      name: result.name,
      planId: result.planId,
    });

    return objectMapper.mapSingle(result);
  };

  const updateTask = async (taskId: number, data: FormFieldsData) => {
    const result = await api.put(`/plantasks/${taskId}`, data);
    onPlanTasksUpdated(result.planId);
    eventsPublisher.publish<PlanTaskEvent>(PLAN_TASK_UPDATED, {
      id: result.id,
      name: result.name,
      planId: result.planId,
    });
    return objectMapper.mapSingle(result);
  };

  const getStandardTaskValidationSchema = () => {
    return {
      name: {
        type: 'string',
        constraints: {
          required: true,
        },
      },
      videoLink: {
        type: 'string',
        constraints: {
          url: true
        }
      },
      dueDateConfig: {
        type: 'object',
        constraints: {},
        properties: {
          days: {
            type: 'int',
            constraints: {
              required: {
                value: true,
                message: 'Field is required',
              }
            },
          },
          when: {
            type: 'string',
            constraints: {
              required: {
                value: true,
                message: 'Field is required',
              },
            },
          },
        },
      },
      parentTaskRelation: {
        type: 'object',
        properties: {
          days: {
            type: 'int',
            constraints: {
              required: true,
              min: {
                value: 1,
                when: {
                  isProceeding: {
                    eq: true
                  }
                },
                message: 'Number of days must be greater than 0',
              }
            },
          },
          parentTaskId: {
            type: 'int',
            constraints: {
              required: true,
            },
          },
        },
        constraints: {},
      },
      transactionParties: {
        type: 'array',
        constraints: {
          required: {
            value: true,
            message: 'At least one transaction party must be selected',
          },
        },
      },
    } as ValidationSchema;
  };

  const getEmailTaskValidationSchema = () => {
    return {
      name: {
        type: 'string',
        constraints: {
          required: true,
        },
      },
      dueDateConfig: {
        type: 'object',
        constraints: {},
        properties: {
          days: {
            type: 'int',
            constraints: {
              required: {
                value: true,
                message: 'Field is required',
              },
            },
          },
          when: {
            type: 'string',
            constraints: {
              required: {
                value: true,
                message: 'Field is required',
              },
            },
          },
        },
      },
      parentTaskRelation: {
        type: 'object',
        properties: {
          days: {
            type: 'int',
            constraints: {
              required: true,
              min: {
                value: 1,
                when: {
                  isProceeding: {
                    eq: true
                  }
                },
                message: 'Number of days must be greater than 0',
              }
            },
          },
          parentTaskId: {
            type: 'int',
            constraints: {
              required: true,
            },
          },
        },
        constraints: {},
      },
      transactionParties: {
        type: 'array',
        constraints: {
          required: {
            value: true,
            message: 'At least one transaction party must be selected',
          },
        },
      },
      emailRecipients: {
        type: 'array',
        constraints: {
          filteredRequired: {
            value: true,
            message: 'At least one email recipient must be selected',
            filter: (item: any) => item.type === 'RECIPIENT',
          },
        },
      },
    } as ValidationSchema;
  };

  const getSmsTaskValidationSchema = () => {
    return {
      name: {
        type: 'string',
        constraints: {
          required: true,
        },
      },
      description: {
        type: 'html',
        constraints: {
          required: true,
        },
      },
      smsRecipients: {
        type: 'array',
        constraints: {
          required: {
            value: true,
            message: 'At least one SMS recipient must be selected',
          },
        },
      },
      dueDateConfig: {
        type: 'object',
        constraints: {},
        properties: {
          days: {
            type: 'int',
            constraints: {
              required: {
                value: true,
                message: 'Field is required',
              }
            },
          },
          when: {
            type: 'string',
            constraints: {
              required: {
                value: true,
                message: 'Field is required',
              },
            },
          },
        },
      },
      parentTaskRelation: {
        type: 'object',
        properties: {
          days: {
            type: 'int',
            constraints: {
              required: true,
              min: {
                value: 1,
                when: {
                  isProceeding: {
                    eq: true
                  }
                },
                message: 'Number of days must be greater than 0',
              }
            },
          },
          parentTaskId: {
            type: 'int',
            constraints: {
              required: true,
            },
          },
        },
        constraints: {},
      },
      transactionParties: {
        type: 'array',
        constraints: {
          required: {
            value: true,
            message: 'At least one transaction party must be selected',
          },
        },
      },
    } as ValidationSchema;
  };

  const getEventTaskValidationSchema = () => {
    return {
      name: {
        type: 'string',
        constraints: {
          required: true,
        },
      },
      transactionParties: {
        type: 'array',
        constraints: {
          required: {
            value: true,
            message: 'At least one transaction party must be selected',
          },
        },
      },
      inviteRecipients: {
        type: 'array',
        constraints: {
          required: {
            value: true,
            message: 'At least one invite recipient must be selected',
          },
        },
      },
    } as ValidationSchema;
  };

  const deleteTask = async (taskId: number) => {
    await api.delete(`/plantasks/${taskId}`);
    eventsPublisher.publish(PLAN_TASKS_UPDATED, {});
    eventsPublisher.publish(PLAN_TASK_DELETED, {
      id: taskId,
    });
  };

  const getTasksCompareFunc = (task1: PlanTask, task2: PlanTask) => {
    const whenWeights = {
      [DueDateWhen.BEFORE_START_DATE]: -4,
      [DueDateWhen.AFTER_START_DATE]: -3,
      [DueDateWhen.BEFORE_END_DATE]: -2,
      [DueDateWhen.AFTER_END_DATE]: -1,
    };

    const dueDateConfig1 = task1.dueDateConfig as DueDateConfig;
    const dueDateConfig2 = task2.dueDateConfig as DueDateConfig;

    let weight1;
    if (dueDateConfig1) {
      weight1 = whenWeights[dueDateConfig1.when];
    } else {
      weight1 = NO_DUE_DATE_WEIGHT;
    }

    let weight2;
    if (dueDateConfig2) {
      weight2 = whenWeights[dueDateConfig2.when];
    } else {
      weight2 = NO_DUE_DATE_WEIGHT;
    }

    function getDays(planTask: PlanTask) {
      const dueDateConfig = planTask.dueDateConfig as DueDateConfig;
      const parentTaskRelation = planTask.parentTaskRelation as PlanTaskParentTaskRelation | undefined;

      const result = dueDateConfig?.days || parentTaskRelation?.days || 0;

      if (parentTaskRelation?.isProceeding) {
        return -result;
      } else {
        return result;
      }
    }

    const days1 = getDays(task1);

    const days2 = getDays(task2);

    const isBeforeDate = dueDateConfig1 && dueDateConfig1.when.startsWith('before_');

    if (weight1 && weight2 && (weight1 !== weight2)) {
      return weight1 - weight2;
    } else if (days1 !== days2) {
      if (isBeforeDate) {
        return days2 - days1;
      } else {
        return days1 - days2;
      }
    } else if (task1.order !== task2.order) {
      return task1.order - task2.order;
    } else {
      return task1.name.localeCompare(task2.name);
    }
  };

  const getPlanTasks = async (planId: number) => {
    const result = await api.get(`/plans/${planId}/tasks`) as PlanTask[];
    result.sort(getTasksCompareFunc);

    return objectMapper.mapList(result);
  };

  const setOrder = async (ids: number[]) => {
    return api.put('/plantasks/bulk/order', {
      ids,
    });
  };

  return {
    getPlanTasks,
    createStandardTask,
    createEmailTask,
    createSmsTask,
    createEventTask,
    getStandardTaskValidationSchema,
    getEmailTaskValidationSchema,
    getSmsTaskValidationSchema,
    getEventTaskValidationSchema,
    updateTask,
    deleteTask,
    setOrder,
    getTasksCompareFunc: () => getTasksCompareFunc,
  };
}
